/**
 * App-wide show/hide state for the Dinnobot assistant, persisted per device.
 *
 * The top-bar toggle (in `TopBar`) and the `GuideWidget` live in different
 * branches of the tree, so the flag is held in a tiny external store rather than
 * lifted through props. `useSyncExternalStore` keeps every consumer in sync
 * within the tab, and a `storage` listener syncs across tabs. Mirrors the
 * localStorage convention used by `use-sidebar-state.ts` (the `hwb:` prefix).
 */
import { useSyncExternalStore } from "react";

const KEY = "hwb:dinnox:hidden";

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

let current = read();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

if (typeof window !== "undefined") {
  // Another tab toggled it — adopt the new value and re-render here too.
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) {
      current = read();
      emit();
    }
  });
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** Set (and persist) whether Dinnobot is hidden app-wide. */
export function setDinnoxHidden(hidden: boolean) {
  if (hidden === current) return;
  current = hidden;
  try {
    localStorage.setItem(KEY, hidden ? "1" : "0");
  } catch {
    /* storage unavailable — non-fatal, stays in-memory for this session */
  }
  emit();
}

/** Read the persisted hide flag and a toggler. `hidden` drives both the
 *  launcher (hidden ⇒ nothing renders) and the top-bar button's dimmed state. */
export function useDinnoxHidden(): { hidden: boolean; toggle: () => void } {
  const hidden = useSyncExternalStore(
    subscribe,
    () => current,
    () => false, // SSR/first-paint default: shown
  );
  return { hidden, toggle: () => setDinnoxHidden(!current) };
}
